import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileSearch, BarChart3, Layers, Upload, Zap, Shield, ArrowRight, ChevronRight } from 'lucide-react';

export default function LandingPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const goAuth = () => navigate('/auth');

  const features = [
    { icon: Upload, title: 'Multi-PDF Upload', desc: 'Upload entire tender packages. Drawings, specs, and schedules processed simultaneously.' },
    { icon: FileSearch, title: 'AI Document Analysis', desc: 'Gemini Pro identifies assemblies, quantities, materials, and dimensions from your plans.' },
    { icon: Layers, title: 'Structured Takeoffs', desc: 'Results organized by CSI Division, Assembly, Component, and Page reference.' },
    { icon: BarChart3, title: 'Cost Estimation', desc: 'Automatic cost estimates using current market rates for all identified components.' },
    { icon: Zap, title: 'Minutes, Not Hours', desc: 'What took days of manual estimation now completes in minutes with AI accuracy.' },
    { icon: Shield, title: 'Reliable & Secure', desc: 'Enterprise-grade security. Your tender documents never leave our secure infrastructure.' },
  ];

  const steps = [
    { num: '01', title: 'Upload Documents', desc: 'Drag & drop your tender PDFs, drawings, and specification documents.' },
    { num: '02', title: 'AI Analyzes', desc: 'Our AI reads every page, identifying quantities, specs, and materials.' },
    { num: '03', title: 'Review Takeoff', desc: 'Get structured results by Division, Assembly, and Component with cost estimates.' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 glass-card" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <img src="/nivela-logo.png" alt="Nivela" className="h-8" />
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-[#446E87]">
            <a href="#features" className="hover:text-[#0D1830] transition-colors">Features</a>
            <a href="#how-it-works" className="hover:text-[#0D1830] transition-colors">How it Works</a>
          </div>
          <Button 
            data-testid="login-btn"
            onClick={goAuth}
            className="bg-[#0D1830] hover:bg-[#1D4053] text-white rounded-full px-6 h-9 text-sm font-medium"
          >
            Sign In
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-24 px-6 relative noise-overlay" data-testid="hero-section">
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <Badge className="mb-6 bg-[#72A4BF]/10 text-[#1D4053] border-[#72A4BF]/20 rounded-full px-4 py-1.5 text-xs font-semibold animate-fade-in" data-testid="hero-badge">
            AI-Powered Construction Estimating
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0D1830] leading-[1.1] tracking-tight animate-fade-in-up">
            Construction Takeoffs<br />
            <span className="text-[#1D4053]">in Minutes, Not Days</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#446E87] max-w-2xl mx-auto leading-relaxed animate-fade-in-up delay-200">
            Upload your tender documents and let AI analyze drawings, specs, and assemblies. 
            Get division-wise, assembly-wise, component-wise takeoffs with cost estimates.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-300">
            <Button
              data-testid="get-started-btn"
              onClick={goAuth}
              size="lg"
              className="bg-[#0D1830] hover:bg-[#1D4053] text-white font-semibold rounded-full px-8 h-12 text-base transition-colors shadow-lg shadow-[#0D1830]/15"
            >
              Get Started Free
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button
              data-testid="learn-more-btn"
              variant="outline"
              size="lg"
              onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              className="rounded-full px-8 h-12 text-base border-[#72A4BF]/30 text-[#1D4053] hover:bg-[#72A4BF]/5"
            >
              See How it Works
            </Button>
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#72A4BF]/6 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#1D4053]/4 rounded-full blur-3xl pointer-events-none" />
      </section>

      {/* Features */}
      <section id="features" className="py-24 px-6 bg-[#F7FAFB]" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#72A4BF]/10 text-[#1D4053] border-[#72A4BF]/20 rounded-full px-3 py-1 text-xs">Features</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0D1830] tracking-tight">Everything You Need</h2>
            <p className="mt-3 text-base text-[#446E87] max-w-lg mx-auto">
              From document upload to detailed cost estimates, all powered by AI.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <div
                key={i}
                className="group p-6 rounded-2xl border border-[#E2E8ED] bg-white hover:border-[#72A4BF]/40 hover:shadow-lg hover:shadow-[#1D4053]/5 transition-all duration-300"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-10 h-10 rounded-xl bg-[#72A4BF]/10 flex items-center justify-center mb-4 group-hover:bg-[#1D4053]/10 transition-colors">
                  <f.icon className="w-5 h-5 text-[#1D4053]" />
                </div>
                <h3 className="text-base font-semibold text-[#0D1830] mb-2">{f.title}</h3>
                <p className="text-sm text-[#446E87] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-24 px-6 bg-white" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#72A4BF]/10 text-[#1D4053] border-[#72A4BF]/20 rounded-full px-3 py-1 text-xs">How It Works</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0D1830] tracking-tight">Three Simple Steps</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <div key={i} className="relative" data-testid={`step-${i}`}>
                <div className="text-5xl font-black text-[#0D1830]/8 mb-3">{s.num}</div>
                <h3 className="text-lg font-semibold text-[#0D1830] mb-2">{s.title}</h3>
                <p className="text-sm text-[#446E87] leading-relaxed">{s.desc}</p>
                {i < 2 && (
                  <ChevronRight className="hidden md:block absolute top-8 -right-5 w-6 h-6 text-[#72A4BF]/40" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-[#0D1830] relative overflow-hidden" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
            Ready to Revolutionize Your Estimates?
          </h2>
          <p className="mt-4 text-base text-[#72A4BF] max-w-lg mx-auto">
            Join contractors and estimators who save hours on every tender with AI-powered takeoffs.
          </p>
          <Button
            data-testid="cta-get-started-btn"
            onClick={goAuth}
            size="lg"
            className="mt-8 bg-white text-[#0D1830] font-semibold rounded-full px-8 h-12 text-base hover:bg-[#F7FAFB] shadow-lg shadow-black/10"
          >
            Start Your First Takeoff
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#1D4053]/20 rounded-full blur-3xl pointer-events-none" />
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-[#0D1830] border-t border-[#1D4053]/30" data-testid="footer">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <img src="/nivela-logo.png" alt="Nivela" className="h-5 brightness-0 invert" />
          </div>
          <p className="text-xs text-[#72A4BF]/60">Built for the construction industry</p>
        </div>
      </footer>
    </div>
  );
}
